import type { ILocalAudioTrack } from 'agora-rtc-sdk-ng';
import type { ILocalTrack, IMicrophoneAudioTrack } from 'agora-rtc-react';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger';

const LOG = new StudioLogger('agoraAudioLogger');

/**
 * Agora SDK Audio Helper Functions
 * These functions provide a consistent interface for Agora audio/video operations
 */

/**
 * Sets the mute status of the local user's audio stream
 * @param localAudioTrack - The Agora local audio track instance
 * @param isMuted - Whether to mute (true) or unmute (false) the audio
 * @returns Promise<void>
 */
export async function muteLocalAudioStream(localAudioTrack: ILocalAudioTrack, isMuted: boolean): Promise<void> {
  if (!localAudioTrack) {
    throw new Error('Local audio track is not initialized');
  }

  try {
    await localAudioTrack.setMuted(isMuted);
    LOG.info(`Agora Local audio ${isMuted ? 'muted' : 'unmuted'}`);
  } catch (error) {
    LOG.error(`Failed to ${isMuted ? 'mute' : 'unmute'} local audio stream:`, error);
    throw error;
  }
}

/**
 * Switches the audio input device for the local audio track
 * @param client - The Agora RTC client instance
 * @param deviceId - The new audio input device ID
 * @returns Promise<IMicrophoneAudioTrack> - The audio track with the switched device
 */
export async function switchAudioInput(client: IAgoraRTCClient, deviceId: string): Promise<IMicrophoneAudioTrack> {
  try {
    if (!client) {
      throw new Error('Agora RTC client is not initialized.');
    }

    const currentAudioTrack = client.localTracks.find(
      (track: ILocalTrack) => track.trackMediaType === 'audio'
    ) as IMicrophoneAudioTrack | null;

    if (!currentAudioTrack) {
      throw new Error('No active audio track found to switch');
    }

    await currentAudioTrack.setDevice(deviceId);
    LOG.info(`Successfully switched audio input device to: ${deviceId}`);
    return currentAudioTrack;
  } catch (error) {
    LOG.error(`Failed to switch audio input device to ${deviceId}:`, error);
    throw error;
  }
}

/**
 * Lists all available audio input devices
 * @returns Promise<MediaDeviceInfo[]> - Array of available audio input devices
 */
export async function listAudioInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await AgoraRTC.getDevices();
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

    return audioInputDevices;
  } catch (error) {
    LOG.error('Failed to list audio input devices:', error);
    throw error;
  }
}
